import React, { useRef, useState, useEffect } from "react";
import cloudUpload from "../../../../assets/img/icon/cloudUpload.svg";
import { toast } from "react-toastify";
import { post } from "../../../../backend/request";
import PageLoader from "../../../shared/PageLoader";
import "./CostDetailsForm.css";

export interface UploadedFileData {
  file: File;
  uploaded_url?: string;
  file_name?: string;
  content_type?: string;
  file_size?: number;
  s3_key?: string;
  s3_bucket?: string;
  s3_region?: string;
}

interface CostDetailsFormProps {
  isOpen: boolean;
  costTypeLabel: string;
  costTypeKey: string;
  onClose: () => void;
  onSave: (costTypeKey: string, data: CostFormData) => void;
  initialData?: CostFormData;
  resourceType?: "license_application" | "company_license";
}

export interface CostFormData {
  cost_description: string;
  cost_amount: string;
  currency: string;
  tax_applicable: boolean;
  tax_amount: string;
  total_cost_including_tax: string;
  vendor_service_provider_name: string;
  payment_status: string;
  cost_period_from: string;
  cost_period_to: string;
  uploaded_files: UploadedFileData[];
}

const getInitialFormData = (): CostFormData => ({
  cost_description: "",
  cost_amount: "",
  currency: "",
  tax_applicable: true,
  tax_amount: "",
  total_cost_including_tax: "",
  vendor_service_provider_name: "",
  payment_status: "",
  cost_period_from: "",
  cost_period_to: "",
  uploaded_files: [],
});

const CostDetailsForm: React.FC<CostDetailsFormProps> = ({
  isOpen,
  costTypeLabel,
  costTypeKey,
  onClose,
  onSave,
  initialData,
  resourceType = "company_license",
}) => {
  const [formData, setFormData] = useState<CostFormData>(
    initialData || getInitialFormData()
  );
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const costPeriodToRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData(initialData || getInitialFormData());
    }
  }, [costTypeKey, isOpen, initialData]);

  useEffect(() => {
    const costAmount = parseFloat(formData.cost_amount) || 0;
    const taxAmount = formData.tax_applicable ? (parseFloat(formData.tax_amount) || 0) : 0;
    const total = costAmount + taxAmount;

    const roundedTotal = Math.round(total * 100) / 100;
    const formattedTotal = parseFloat(roundedTotal.toFixed(2)).toString();

    setFormData((prev) => ({
      ...prev,
      total_cost_including_tax: formattedTotal,
    }));
  }, [formData.cost_amount, formData.tax_amount, formData.tax_applicable]);

  const handleInputChange = (field: keyof CostFormData, value: any) => {
    if (field === "tax_applicable" && value === false) {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        tax_amount: "",
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/svg+xml", "application/pdf"];
    const allowedExtensions = [".jpg", ".jpeg", ".png", ".webp", ".svg", ".pdf"];

    // Check max file count (5 files max)
    const currentFileCount = formData.uploaded_files.length;
    const newFileCount = files.length;
    const totalFileCount = currentFileCount + newFileCount;

    if (totalFileCount > 5) {
      toast.error(`You can upload a maximum of 5 files. Currently ${currentFileCount} file${currentFileCount !== 1 ? "s" : ""} uploaded.`);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const validFiles = files.filter((file) => {
      const nameParts = file.name.split(".");
      const extension = nameParts.length > 1 ? nameParts[nameParts.length - 1].toLowerCase() : "";
      const fileExtension = "." + extension;
      const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);

      if (!isValidType) {
        toast.error(`File ${file.name} is not a supported format`);
        return false;
      }
      if (file.size > 15 * 1024 * 1024) {
        toast.error(`File ${file.name} exceeds 15MB limit`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      const newUploadedFiles: UploadedFileData[] = validFiles.map((file) => ({
        file,
        uploaded_url: undefined,
        file_name: file.name,
      }));
      setFormData((prev) => ({
        ...prev,
        uploaded_files: [...prev.uploaded_files, ...newUploadedFiles],
      }));
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      uploaded_files: prev.uploaded_files.filter((_, i) => i !== index),
    }));
  };

  const handleChooseFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const validateForm = (): boolean => {
    if (!formData.cost_description || !formData.cost_description.trim()) {
      toast.error("Please enter cost description");
      return false;
    }
    if (!formData.cost_amount || !formData.cost_amount.trim()) {
      toast.error("Please enter cost amount");
      return false;
    }
    if (formData.tax_applicable && (!formData.tax_amount || !formData.tax_amount.trim())) {
      toast.error("Please enter tax amount");
      return false;
    }
    if (!formData.total_cost_including_tax || !formData.total_cost_including_tax.trim()) {
      toast.error("Total cost is required");
      return false;
    }
    if (!formData.vendor_service_provider_name || !formData.vendor_service_provider_name.trim()) {
      toast.error("Please enter vendor/service provider name");
      return false;
    }
    if (!formData.payment_status || !formData.payment_status.trim()) {
      toast.error("Please enter payment status");
      return false;
    }
    if (!formData.cost_period_from) {
      toast.error("Please select cost period from date");
      return false;
    }
    if (!formData.cost_period_to) {
      toast.error("Please select cost period to date");
      return false;
    }
    if (formData.uploaded_files.length === 0) {
      toast.error("Please upload at least one supporting document");
      return false;
    }
    return true;
  };

  const uploadFile = async (fileData: UploadedFileData): Promise<UploadedFileData> => {
    // Skip if already uploaded
    if (fileData.uploaded_url) {
      return fileData;
    }

    const uploadFormData = new FormData();
    uploadFormData.append("file", fileData.file);
    uploadFormData.append("resource_type", resourceType);
    uploadFormData.append("file_type", `${resourceType}/cost_detail_invoice`);
    uploadFormData.append("resource_field", "cost_detail_invoice");
    uploadFormData.append("skip_deep_scan", "true");

    try {
      const response = await post(
        "/api/v1/upload",
        uploadFormData,
        { "Content-Type": "multipart/form-data" },
        true
      );

      const responseData = response && response.data;
      if (responseData && responseData.url) {
        const fileInfo = responseData.file_info || {};
        const url = responseData.url;
        let s3Key = "";
        let s3Bucket = "";
        let s3Region = "";

        const s3Match = url.match(/https:\/\/([^.]+)\.s3\.([^.]+)\.amazonaws\.com\/(.+)/);
        if (s3Match) {
          s3Bucket = s3Match[1];
          s3Region = s3Match[2];
          s3Key = decodeURIComponent(s3Match[3]);
        }

        return {
          ...fileData,
          uploaded_url: responseData.url,
          file_name: fileInfo.filename || fileData.file.name,
          content_type: fileInfo.content_type || fileData.file.type,
          file_size: fileInfo.size || fileData.file.size,
          s3_key: s3Key,
          s3_bucket: s3Bucket,
          s3_region: s3Region,
        };
      }
      throw new Error("No URL in response");
    } catch (error: any) {
      const errorMessage = error && error.response && error.response.data && error.response.data.details
        ? error.response.data.details.join(", ")
        : `Failed to upload ${fileData.file.name}`;
      toast.error(`${errorMessage}: ${fileData.file.name}`);
      return fileData;
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    // Check if there are files that need uploading
    const filesToUpload = formData.uploaded_files.filter((f) => !f.uploaded_url);

    if (filesToUpload.length > 0) {
      setIsUploading(true);
      try {
        // Upload all files in parallel
        const uploadPromises = formData.uploaded_files.map((fileData) => uploadFile(fileData));
        const uploadedFiles = await Promise.all(uploadPromises);

        const allFilesUploaded = uploadedFiles.every((file) => file.uploaded_url);

        if (!allFilesUploaded) {
          return;
        }

        // Update form data with uploaded URLs and call onSave
        const updatedFormData = {
          ...formData,
          uploaded_files: uploadedFiles,
        };
        onSave(costTypeKey, updatedFormData);
      } catch (error) {
        console.error("Failed to upload one or more files. Please try again.");
      } finally {
        setIsUploading(false);
      }
    } else {
      onSave(costTypeKey, formData);
    }
  };

  const handleCancel = () => {
    setFormData(initialData || getInitialFormData());
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="cost-details-form-wrapper">
      <div className="license-popup-overlay"></div>
      <div className="cost-details-popup">
        <div className="popup-header d-flex justify-content-between align-items-start">
          <h5 className="mb-0 fw-semibold">Add Cost Details for: {costTypeLabel}</h5>
          <button
            className="btn-close"
            aria-label="Close"
            onClick={handleCancel}
          ></button>
        </div>

        <hr className="mb-3 mt-0" />

        <div className="popup-body">
          {/* Cost Description */}
          <div className="mb-3">
            <label className="form-label">Cost Description<span>*</span></label>
            <textarea
              className="form-control cost-description-textarea"
              placeholder="Enter cost description"
              value={formData.cost_description}
              onChange={(e) => handleInputChange("cost_description", e.target.value)}
              style={{ minHeight: "100px"}}
            />
          </div>

          {/* Cost Amount and Currency - Side by Side */}
          <div className="d-flex gap-3 mb-3">
            <div className="flex-fill">
              <label className="form-label">
                Cost Amount<span>*</span>
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="Add cost amount"
                value={formData.cost_amount}
                onChange={(e) => handleInputChange("cost_amount", e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                min="1"
              />
            </div>
            {/* <div className="flex-fill">
              <label className="form-label">
                Choose Currency<span>*</span>
              </label>
              <select
                className="form-select"
                value={formData.currency}
                onChange={(e) => handleInputChange("currency", e.target.value)}
              >
                <option value="">Choose currency</option>
                {CURRENCY_OPTIONS.map((currency) => (
                  <option key={currency.id} value={currency.id}>
                    {currency.label}
                  </option>
                ))}
              </select>
            </div> */}
          </div>

          {/* Tax Applicable Toggle */}
          <div className="mb-3 d-flex align-items-center justify-content-between">
            <div>
              <label className="form-label mb-0">Tax Applicable</label>
              <p className="mb-0 text-gray-12 text-sm">
                Enable this toggle if this cost is including tax
              </p>
            </div>
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="taxApplicable"
                checked={formData.tax_applicable}
                onChange={(e) => handleInputChange("tax_applicable", e.target.checked)}
              />
            </div>
          </div>

          {/* Tax Amount - Only show when tax_applicable is true */}
          {formData.tax_applicable && (
            <div className="flex-fill mb-3">
              <label className="form-label">
                Tax Amount<span>*</span>
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="Add tax amount"
                value={formData.tax_amount}
                onChange={(e) => handleInputChange("tax_amount", e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                min="1"
              />
            </div>
          )}

          {/* Total Cost Including Tax - Auto calculated, read-only */}
          <div className="mb-3">
            <label className="form-label">
              Total Cost {formData.tax_applicable ? "(Including Tax)" : ""}<span>*</span>
            </label>
            <input
              type="number"
              className="form-control bg-light"
              placeholder="Auto calculated total cost"
              value={formData.total_cost_including_tax}
              onWheel={(e) => e.currentTarget.blur()}
              readOnly
              disabled
            />
          </div>

          {/* Vendor/Service Provider Name */}
          <div className="mb-3">
            <label className="form-label">
              Vendor/Service Provider Name<span>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Add vendor/service provider name"
              value={formData.vendor_service_provider_name}
              onChange={(e) => handleInputChange("vendor_service_provider_name", e.target.value)}
            />
          </div>

          {/* Payment Status */}
          <div className="mb-3">
            <label className="form-label">
              Payment Status<span>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Add payment status"
              value={formData.payment_status}
              onChange={(e) => handleInputChange("payment_status", e.target.value)}
            />
          </div>

          {/* Cost Period */}
          <div className="mb-3">
            <label className="form-label">Cost Period<span>*</span></label>
            <div className="d-flex gap-3 align-items-center">
              <input
                type="date"
                className="form-control"
                value={formData.cost_period_from}
                max={formData.cost_period_to || undefined}
                onChange={(e) => {
                  handleInputChange("cost_period_from", e.target.value);
                  if (e.target.value && costPeriodToRef.current) {
                    const toInput = costPeriodToRef.current;
                    setTimeout(() => {
                      if (toInput) {
                        toInput.showPicker();
                      }
                    }, 100);
                  }
                }}
              />
              <span className="text-gray-12">To</span>
              <input
                ref={costPeriodToRef}
                type="date"
                className="form-control"
                value={formData.cost_period_to}
                min={formData.cost_period_from || undefined}
                disabled={!formData.cost_period_from}
                onChange={(e) => handleInputChange("cost_period_to", e.target.value)}
              />
            </div>
          </div>

          {/* Upload Invoice / Supporting Document */}
          <div className="mb-3">
            <label className="form-label">Upload Invoice / Supporting Document<span>*</span></label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.svg,.pdf"
              onChange={handleFileSelect}
              style={{ display: "none" }}
              multiple
            />

            {formData.uploaded_files.length < 5 && (
              <div
                className="p-4 text-center upload-box"
                onClick={handleChooseFile}
              >
                <div className="mb-2">
                  <img src={cloudUpload} alt="cloudUpload" className="me-2" />
                </div>
                <p className="mb-2 text-md text-gray-12">
                  Click to browse and upload
                </p>
                <button
                  type="button"
                  className="mb-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChooseFile();
                  }}
                >
                  Choose File
                </button>
                <div>
                  <small className="text-gray-12 d-block">
                    Supported formats (Up to 15 MB per file, max 5 files): JPG, PNG, WEBP, SVG, PDF, JPEG
                  </small>
                </div>
              </div>
            )}

            {formData.uploaded_files.length > 0 &&
              formData.uploaded_files.map((fileData, index) => (
                <div
                  key={index}
                  className="upload-successful position-relative mb-2 mt-2"
                >
                  <div
                    className="close-button position-absolute"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile(index);
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M10.0002 5.99992L6.00016 9.99992M6.00016 5.99992L10.0002 9.99992M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z"
                        stroke="#757575"
                        strokeWidth="1.33333"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="p-3 d-flex align-items-center">
                    <p className="text-sm m-0 ellipse">{fileData.file_name || fileData.file.name}</p>
                  </div>
                </div>
              ))}
          </div>
        </div>

        <hr className="my-3" />

        {/* Footer Buttons */}
        <div className="popup-footer d-flex gap-3">
          <button
            type="button"
            className="button-secondary flex-fill"
            onClick={handleCancel}
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button-primary flex-fill"
            onClick={handleSave}
            disabled={isUploading}
          >
            {isUploading ? "Uploading..." : "Done"}
          </button>
        </div>
      </div>
      {isUploading && <PageLoader />}
    </div>
  );
};

export default CostDetailsForm;
